/**
 * Creating a new game state.
 *
 * As of Phase 1 this produces a real, fully generated world: plates, coastlines,
 * climate, terrain, features, rivers and resources. What it does not yet produce is
 * anything that belongs to a civilisation — no starting positions, no units, no
 * cities. Those arrive in Phase 3, when there is a turn engine to move them.
 */

import { loadContent } from '../../../content/schema/index.js';
import { chance, createRng, nextInt, type RngState } from '../rng/index.js';
import { generateWorld, type WorldReport } from '../map/generate.js';
import { resolveMapConfig, type MapgenOverrides } from '../map/params.js';
import { createGrid } from './grid.js';
import { indexToOffset } from './grid.js';
import { chooseStartPositions } from './start.js';
import { NONE, type Civ, type GameState } from './types.js';
import { CURRENT_SCHEMA } from '../save/migrations.js';
import { spawnUnit } from '../units/index.js';
import { createMemory, createVisionPlane, recomputeVision } from '../vision/index.js';

/**
 * The named random streams the engine uses. Adding one here is how a new system gets
 * randomness of its own, without disturbing the sequence any other system sees.
 */
export const RNG_STREAMS = ['mapgen', 'resources', 'start', 'combat', 'ai', 'events'] as const;
export type RngStreamName = (typeof RNG_STREAMS)[number];

export interface NewGameOptions {
  seed: string;
  /** A key from mapgen-presets.json, e.g. "standard". */
  size?: string;
  wrapX?: boolean;
  /** Overrides for individual generation parameters — used by the debug panel and map tool. */
  mapgen?: MapgenOverrides;
  /**
   * Place starting civilisations with their first units. Off for the map tools, which
   * want a world rather than a game.
   */
  withCiv?: boolean;
  /** How many civilisations to place, the human's included. */
  civs?: number;
  /**
   * The human's civilisation, as a key from civilizations.json (ADR-0062). Rivals are
   * then drawn at random from the rest. Absent, civilisations are seated in file order —
   * which simulations and tests rely on.
   */
  civType?: string;
  /** A key from mapgen-presets.json's mapTypes: continents, pangaea, archipelago. */
  mapType?: string;
}

/** The generation overrides a kind of world implies, under any explicit ones. */
function mapTypeOverrides(content: ReturnType<typeof loadContent>, key: string | undefined): MapgenOverrides {
  if (key === undefined) return {};
  const type = content.mapgen.mapTypes.find((t) => t.key === key);
  if (!type) throw new Error(`Unknown map type "${key}". Known: ${content.mapgen.mapTypes.map((t) => t.key).join(', ')}`);
  return type.overrides as MapgenOverrides;
}

/**
 * Which civilisation sits in each seat. With a chosen civilisation the human takes it
 * and the rivals are a seeded draw from the others, so the same seed gives the same
 * opponents; without one, seats follow the file.
 */
export function chooseRoster(content: ReturnType<typeof loadContent>, seed: string, count: number, civType?: string): number[] {
  const all = content.civilizations.all.map((c) => c.id);
  if (civType === undefined) return Array.from({ length: count }, (_, seat) => all[seat % all.length]!);
  const human = content.civilizations.id(civType);
  const others = all.filter((id) => id !== human);
  const draw = createRng(seed, 'roster');
  for (let i = others.length - 1; i > 0; i--) {
    const j = nextInt(draw, 0, i + 1);
    [others[i], others[j]] = [others[j]!, others[i]!];
  }
  return [human, ...others].slice(0, count);
}

export function createRngStreams(seed: string): Record<string, RngState> {
  const streams: Record<string, RngState> = {};
  for (const name of RNG_STREAMS) streams[name] = createRng(seed, name);
  return streams;
}

/**
 * Create a game, and return the generation report alongside it.
 *
 * The report is diagnostic only — land and water counts, continent sizes, river and
 * resource tallies. It is never stored in the save, because it is derivable from the
 * map and stale copies of derived data are how save formats rot.
 */
export function newGameWithReport(options: NewGameOptions): { state: GameState; report: WorldReport } {
  const content = loadContent();
  const { size, params } = resolveMapConfig(options.size, { ...mapTypeOverrides(content, options.mapType), ...options.mapgen });

  const rng = createRngStreams(options.seed);
  const map = createGrid(size.width, size.height, options.wrapX ?? params.wrapX);

  const { report } = generateWorld(map, content, params, rng['mapgen']!);

  const state: GameState = {
    meta: {
      seed: options.seed,
      turn: 1,
      activeCiv: 0,
      schema: CURRENT_SCHEMA,
      createdAt: Date.now(),
    },
    map,
    civs: {},
    cities: {},
    units: {},
    vision: {},
    memory: {},
    relations: [],
    payments: [],
    grants: [],
    offers: [],
    result: null,
    history: { turns: [], civs: {} },
    nextId: 1,
    rng,
  };

  if (options.withCiv !== false) placeStartingCivs(state, content, options.civs ?? 1, options.civType);

  return { state, report };
}

/**
 * Place the starting civilisations and their opening units.
 *
 * Civilisations come from `content/civilizations.json` — real cultures with real city
 * names, per ADR-0008. Only the first is human; the rest sit still until the AI
 * arrives in Phase 5, and are here now so that start separation and border ownership
 * are exercised by more than one player before anything depends on them.
 *
 * Each civilisation opens with a settler, a warrior and a scout: something to found
 * with, something to defend it, and something to go and look.
 */
function placeStartingCivs(state: GameState, content: ReturnType<typeof loadContent>, count: number, civType?: string): void {
  const wanted = Math.max(1, Math.min(count, content.civilizations.all.length));
  const starts = chooseStartPositions(state.map, content, wanted);
  const roster = chooseRoster(content, state.meta.seed, wanted, civType);

  // Its own stream, made here and not stored: personalities are drawn once, at the
  // start, and must not shift any random sequence the game itself goes on to use.
  const twist = createRng(state.meta.seed, 'personality');

  starts.forEach((start, seat) => {
    const definition = content.civilizations.get(roster[seat]!);
    let personality = definition.personalityId;
    if (chance(twist, content.rules.ai.personalityTwist) && content.personalities.all.length > 1) {
      // Any of the others, never the one it leans to.
      const others = content.personalities.all.filter((p) => p.id !== personality);
      personality = others[nextInt(twist, 0, others.length)]!.id;
    }
    const civ: Civ = {
      id: state.nextId++,
      civType: definition.id,
      name: definition.name,
      isHuman: seat === 0,
      treasury: content.rules.economy.startingTreasury,
      researchRate: content.rules.research.startingRate,
      techs: [...definition.startingTechIds],
      researching: NONE,
      researchGoal: NONE,
      researchStore: 0,
      defeated: false,
      personality,
      colour: definition.colour,
      government: content.governments.id(content.rules.government.start),
      nextGovernment: NONE,
      anarchyEnds: 0,
      governmentSince: 1,
      weariness: 0,
      reputation: 0,
    };
    state.civs[civ.id] = civ;
    if (seat === 0) state.meta.activeCiv = civ.id;

    state.vision[civ.id] = createVisionPlane(state.map);
    state.memory[civ.id] = createMemory(state.map);

    const { col, row } = indexToOffset(state.map, start);
    spawnUnit(state, content, civ.id, 'settler', col, row);
    spawnUnit(state, content, civ.id, 'warrior', col, row);
    spawnUnit(state, content, civ.id, 'scout', col, row);

    recomputeVision(state, content, civ.id);
  });
}

export function newGame(options: NewGameOptions): GameState {
  return newGameWithReport(options).state;
}
